import { Link } from "react-router-dom";
import { Wrench, TrendingDown, Clock, Sparkles, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { useLang } from "../context/LanguageContext";

export function MaintenancePromo() {
  const { t, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const m = t.maintenancePromo;

  return (
    <section className="relative py-24 bg-gradient-to-br from-amber-50/60 via-white to-red-50/30 overflow-hidden" data-testid="maintenance-promo">
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: "repeating-linear-gradient(-45deg, #ef4444, #ef4444 1px, transparent 1px, transparent 24px)",
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          {/* LEFT */}
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 shadow-sm rounded-full px-4 py-1.5 text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-4 uppercase">
              <Wrench className="w-3.5 h-3.5" />
              {m.eyebrow}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-5 text-[#0891b2]">
              {m.title}
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8 max-w-2xl">{m.subtitle}</p>

            {/* Feature list */}
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-8">
              {m.features.map((f, i) => (
                <div key={i} data-testid={`mp-feat-${i}`} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700">{f}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Link to="/maintenance">
                <Button data-testid="mp-cta" className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-12 px-6 font-semibold shadow-md">
                  {m.cta} <Arrow className="w-4 h-4 ms-2" />
                </Button>
              </Link>
              <Link to="/maintenance#plans">
                <Button variant="outline" data-testid="mp-plans" className="border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white h-12 px-6 font-semibold">
                  {m.viewPlans}
                </Button>
              </Link>
            </div>
          </div>

          {/* RIGHT — 3 stat cards */}
          <div className="lg:col-span-2 grid grid-cols-2 gap-4">
            {[
              { Icon: TrendingDown, value: "75%", label: m.stat1, color: "bg-red-50 text-[#ef4444]" },
              { Icon: Clock, value: "50%", label: m.stat2, color: "bg-cyan-50 text-[#0891b2]" },
              { Icon: Sparkles, value: "30%", label: m.stat3, color: "bg-amber-50 text-amber-700", full: true },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`mp-stat-${i}`}
                className={`${s.full ? "col-span-2" : ""} bg-white rounded-2xl p-5 border border-slate-100 shadow-sm`}
              >
                <div className={`w-10 h-10 rounded-lg ${s.color} flex items-center justify-center mb-3`}>
                  <s.Icon className="w-5 h-5" />
                </div>
                <div className="font-display text-4xl text-[#0891b2] mb-1 leading-none">{s.value}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
